import { loadModules } from 'esri-loader';
import React from 'react';
import ReactDOM from 'react-dom';
import BSLAExternalLinkComponent from '../../helpers/BSLAExternalLinkComponent';
import { DateTime } from 'luxon';
import format from 'number-format.js';
import BSLAPopupItemComponent from '../../helpers/BSLAPopupItemComponent';
import { REGION_SCALE } from '../../BSLAConstants';

const desc =
    'Development rights have been given away during previous permitting. Generally a developer gives up the conservation easement for mitigation or to provide a preserve that offsets the development right granted.';

const sfwmdPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Grantor" value={atts.GRANTOR !== ' ' ? atts.GRANTOR : 'N/A'} />
            <BSLAPopupItemComponent label="Easement Number" value={atts.CE_NO} />
            <BSLAPopupItemComponent label="Easement Type" value={atts.CE_TYPE} />
            <BSLAPopupItemComponent label="Deeded Acerage" value={atts.RECORDED_A > 0 ? atts.RECORDED_A : 'N/A'} />
            <BSLAPopupItemComponent label="Date Recorded" value={atts.RECORDED_D ? DateTime.fromMillis(atts.RECORDED_D).toFormat('MMM dd, yyyy') : 'N/A'} />
            <div className="mt-3 mb-1">{desc}</div>
            <BSLAExternalLinkComponent href={atts.LINK} />
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const sjrwmdPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');

    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Grantor" value={atts.GRANTOR} />
            <BSLAPopupItemComponent label="Tract ID" value={atts.TRACT_ID} />
            <BSLAPopupItemComponent label="Easement Name" value={atts.CONSERVATI} />
            <BSLAPopupItemComponent label="Deed Type" value={atts.DEED_TYPE} />
            <BSLAPopupItemComponent label="Deeded Acerage" value={atts.DEED_ACRES > 0 ? atts.DEED_ACRES : 'N/A'} />
            <BSLAPopupItemComponent label="Comments" value={atts.COMMENTS ?? 'N/A'} />
            <div className="mt-3">{desc}</div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const hillsboroughPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');

    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Site Name" value={atts.SITE_NAME !== ' ' ? atts.SITE_NAME : 'N/A'} />
            <BSLAPopupItemComponent label="Former Site Name" value={atts.SITE_FKA !== ' ' ? atts.SITE_FKA : 'N/A'} />
            <BSLAPopupItemComponent label="Site Number" value={atts.SITE_NO !== ' ' ? atts.SITE_NO : 'N/A'} />
            <BSLAPopupItemComponent label="Type" value={atts.TYPE} />
            <div className="mt-3">{desc}</div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const manateePopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');

    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Label" value={atts.GIS_LABEL} />
            <div className="mt-3">{desc}</div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const nwfwmdPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');

    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Name" value={atts.NAME} />
            <BSLAPopupItemComponent label="Type" value={atts.TYPE} />
            <div className="mt-3">{desc}</div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const swfwmdPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');

    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Name" value={atts.PROJECTNAME} />
            <BSLAPopupItemComponent label="Status" value={atts.ACQSTATUS} />
            <BSLAPopupItemComponent label="Acerage" value={format('###,###.00', atts.ACRES)} />
            <div className="mt-3 mb-1">{desc}</div>
            <BSLAExternalLinkComponent href={atts.LINK} />
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const sarasotaPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Notes" value={atts.notes} />
            <BSLAPopupItemComponent label="Tax Year Change" value={atts.tax_yr_chng ?? 'N/A'} />
            <div className="mt-3">{desc}</div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const conseEaseInfos = [
    {
        title: 'Conservation Easements (SFWMD)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/e1d015fb76e84cc08112e42a132b93f4/rest/services/CONSERVATION_EASEMENTS_V2/FeatureServer/1',
        popupContent: { content: sfwmdPopupContent }
    },
    {
        title: 'Conservation Easements (SJRWMD)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/e1d015fb76e84cc08112e42a132b93f4/rest/services/CONSERVATION_EASEMENTS_V2/FeatureServer/0',
        popupContent: { content: sjrwmdPopupContent }
    },
    {
        title: 'Conservation Easements (Hillsborough)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/e1d015fb76e84cc08112e42a132b93f4/rest/services/CONSERVATION_EASEMENTS_V2/FeatureServer/3',
        popupContent: { content: hillsboroughPopupContent }
    },
    {
        title: 'Conservation Easements (Manatee)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/e1d015fb76e84cc08112e42a132b93f4/rest/services/CONSERVATION_EASEMENTS_V2/FeatureServer/2',
        popupContent: { content: manateePopupContent }
    },
    {
        title: 'Conservation Easements (NWFWMD)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/e1d015fb76e84cc08112e42a132b93f4/rest/services/CONSERVATION_EASEMENTS_V2/FeatureServer/4',
        popupContent: { content: nwfwmdPopupContent }
    },
    {
        title: 'Conservation Easements (SWFWMD)',
        url: 'https://www25.swfwmd.state.fl.us/arcgis12/rest/services/OpenData/LandResources/MapServer/0',
        popupContent: { content: swfwmdPopupContent }
    },
    {
        title: 'Conservation Easements (Sarasota)',
        url: 'https://ags3.scgov.net/server/rest/services/Hosted/Easement/FeatureServer/0',
        popupContent: { content: sarasotaPopupContent },
        definitionExpression: 'easement_type = 3'
    }
];

export const sortInfos = (a: any, b: any) => {
    if (a.title.toLowerCase() > b.title.toLowerCase()) {
        return -1;
    }

    if (a.title.toLowerCase() < b.title.toLowerCase()) {
        return 1;
    }

    return 0;
};

const BSLAConsEaseLayers = async () => {
    type layerModules = [
        typeof import('esri/layers/GroupLayer'),
        typeof import('esri/layers/FeatureLayer'),
        typeof import('esri/symbols/SimpleFillSymbol'),
        typeof import('esri/symbols/SimpleLineSymbol'),
        typeof import('esri/renderers/SimpleRenderer')
    ];

    const [GroupLayer, FeatureLayer, SimpleFillSymbol, SimpleLineSymbol, SimpleRenderer] = await (loadModules([
        'esri/layers/GroupLayer',
        'esri/layers/FeatureLayer',
        'esri/symbols/SimpleFillSymbol',
        'esri/symbols/SimpleLineSymbol',
        'esri/renderers/SimpleRenderer'
    ]) as Promise<layerModules>);

    const lineSymbol = new SimpleLineSymbol({
        color: [230, 0, 0, 1],
        width: 1
    });

    const fillSymbol = new SimpleFillSymbol({
        color: [0, 0, 0, 0],
        outline: lineSymbol
    });

    const renderer = new SimpleRenderer({ symbol: fillSymbol });

    const layers = conseEaseInfos.sort(sortInfos).map((i) => {
        return new FeatureLayer({
            minScale: REGION_SCALE,
            url: i.url,
            title: i.title,
            visible: false,
            renderer,
            definitionExpression: i.definitionExpression ?? '1=1',
            popupTemplate: {
                outFields: ['*'],
                title: i.title,
                ...i.popupContent
            }
        });
    });

    return new GroupLayer({
        layers: layers,
        title: 'Conservation Easements (Various)'
    });
};

export default BSLAConsEaseLayers;
